import { useCallback, useState } from 'react'
import { Button } from '@/atoms/Button'
import { LoadingSpinnerIcon } from '@/atoms/Icons/LoadingSpinnerIcon'
import { Modal } from '@/molecules/Modal'
import { useTranslate } from '@/utils/translate/translate-client'
import { Input } from '../../components/Inputs'
import { useCreateProjectMutation } from './hooks/useCreateProjectMutation'
import { useUpdateTorchApplicationProject } from './hooks/useUpdateTorchApplicationProject'

interface Props {
  applicationId?: string
  showModal: boolean
  closeModal: () => void
  onSelectedProjectId: (projectId: string) => void
}

export const AssociatedProjectModal = ({ applicationId, showModal, closeModal, onSelectedProjectId }: Props) => {
  const { t } = useTranslate('common')
  const [projectName, setProjectName] = useState('')
  const [updateTorchApplicationProject] = useUpdateTorchApplicationProject()

  const [createProject, { loading: createLoading }] = useCreateProjectMutation()
  const handleOnClick = useCallback(
    async (projectName: string) => {
      const projectData = await createProject({
        variables: {
          input: {
            name: projectName,
          },
        },
      })

      if (applicationId && projectData.data?.createProject?.id) {
        await updateTorchApplicationProject({
          variables: {
            input: {
              id: applicationId,
              projectId: projectData.data.createProject.id,
            },
          },
        })

        onSelectedProjectId(projectData.data.createProject.id)
      }

      closeModal()
    },
    [applicationId, closeModal, createProject, onSelectedProjectId, updateTorchApplicationProject],
  )

  return (
    <Modal isOpen={showModal} onClose={closeModal}>
      <div className="flex flex-col gap-4">
        <Input
          label={t('projectName', 'Project Name')}
          value={projectName}
          onChange={(val) => setProjectName(val.currentTarget.value)}
          name="title"
          required
        />
        <div className="flex items-center justify-between">
          <Button variant="lightgray" onClick={closeModal}>
            {t('cancel', 'Cancel')}
          </Button>
          <Button
            variant="offblack"
            onClick={() => handleOnClick(projectName)}
            disabled={!projectName || !applicationId || createLoading}
          >
            {createLoading ? <LoadingSpinnerIcon color="black" size={12} /> : t('createProject', 'Create project')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
